import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Key, Save, Eye, EyeOff, Check, X, Shield, MessageSquare, Send, Loader2, ExternalLink, Info, Bell, Mail, Clock, AlertTriangle, Plus, Trash2, Pencil, GripVertical, ArrowUp, ArrowDown, Globe } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Sortable Rule Item Component for drag-and-drop
const SortableRuleItem = ({ rule, index, isDarkMode, editingRuleId, editingRule, setEditingRule, cancelEditRule, saveEditRule, toggleRewriteRule, startEditRule, removeRewriteRule, moveRuleUp, moveRuleDown, totalRules }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id, disabled: editingRuleId === rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 rounded-xl transition-all ${
        isDragging ? 'opacity-50 scale-95 shadow-lg' : ''
      } ${
        rule.enabled
          ? isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'
          : isDarkMode ? 'bg-[hsl(220,14%,16%)] opacity-60' : 'bg-slate-100 opacity-60'
      }`}
    >
      {editingRuleId === rule.id ? (
        /* Mode édition */
        <div className="flex-1 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>Pattern</label>
              <Input
                value={editingRule.pattern}
                onChange={(e) => setEditingRule({...editingRule, pattern: e.target.value})}
                className={`rounded-lg h-9 mt-1 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
              />
            </div>
            <div>
              <label className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>Remplacement</label>
              <Input
                value={editingRule.replacement}
                onChange={(e) => setEditingRule({...editingRule, replacement: e.target.value})}
                className={`rounded-lg h-9 mt-1 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
              />
            </div>
            <div>
              <label className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>Description</label>
              <Input
                value={editingRule.description}
                onChange={(e) => setEditingRule({...editingRule, description: e.target.value})}
                className={`rounded-lg h-9 mt-1 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={cancelEditRule}
              className={isDarkMode ? 'text-slate-400 hover:text-slate-300' : ''}
            >
              <X className="h-4 w-4 mr-1" />
              Annuler
            </Button>
            <Button
              size="sm"
              onClick={saveEditRule}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              <Check className="h-4 w-4 mr-1" />
              Enregistrer
            </Button>
          </div>
        </div>
      ) : (
        /* Mode affichage */
        <>
          {/* Drag handle + position */}
          <div 
            className="flex items-center gap-2 mr-3 cursor-grab active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripVertical className={`h-5 w-5 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`} />
            <Badge variant="outline" className={`text-xs px-1.5 ${isDarkMode ? 'border-slate-600 text-slate-500' : ''}`}>
              {index + 1}
            </Badge>
          </div>

          {/* Rule content */}
          <div className="flex items-center gap-4 flex-1">
            <Switch
              checked={rule.enabled}
              onCheckedChange={() => toggleRewriteRule(rule.id)}
            />
            <div>
              <div className="flex items-center gap-2">
                <code className={`px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-[hsl(220,14%,22%)] text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                  {rule.pattern}
                </code>
                <span className={isDarkMode ? 'text-slate-400' : 'text-slate-400'}>→</span>
                <code className={`px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-[hsl(220,14%,22%)] text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                  {rule.replacement}
                </code>
              </div>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>
                {rule.description}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            {/* Move Up */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => moveRuleUp(index)}
              disabled={index === 0}
              className={`${isDarkMode ? 'text-slate-400 hover:text-slate-300 disabled:text-slate-600' : 'text-slate-500 hover:text-slate-700 disabled:text-slate-300'}`}
              title="Monter (plus prioritaire)"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
            {/* Move Down */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => moveRuleDown(index)}
              disabled={index === totalRules - 1}
              className={`${isDarkMode ? 'text-slate-400 hover:text-slate-300 disabled:text-slate-600' : 'text-slate-500 hover:text-slate-700 disabled:text-slate-300'}`}
              title="Descendre (moins prioritaire)"
            >
              <ArrowDown className="h-4 w-4" />
            </Button>
            {/* Edit */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => startEditRule(rule)}
              className={`${isDarkMode ? 'text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10' : 'text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50'}`}
              title="Modifier"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {/* Delete */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeRewriteRule(rule.id)}
              className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const Settings = ({ embedded = false }) => {
  const { user, isAdmin, isSuperAdmin, token } = useAuth();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    project_id: '',
    service_account_email: '',
    agent_id: '',
    brand_id: '',
    private_key: ''
  });

  // Client permissions (to check if SMS is enabled for this client)
  const [clientPermissions, setClientPermissions] = useState(null);

  // SMS Config state (B2SMS) - Per Client
  const [smsConfig, setSmsConfig] = useState({
    enabled: false,
    sender_id: '',
    custom_message: '',
    credentials: {
      login: '',
      password: '',
      service_url: 'https://www.b2sms.com/v1'
    },
    is_configured: false
  });
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsTestPhone, setSmsTestPhone] = useState('');
  const [smsTesting, setSmsTesting] = useState(false);
  const [showSmsPassword, setShowSmsPassword] = useState(false);

  // Phone rewrite rules state (SUPADMIN only)
  const [phoneRewriteRules, setPhoneRewriteRules] = useState([
    { id: 1, pattern: '0692', replacement: '+262692', description: 'Réunion mobile 0692', enabled: true },
    { id: 2, pattern: '0693', replacement: '+262693', description: 'Réunion mobile 0693', enabled: true },
    { id: 3, pattern: '0639', replacement: '+262639', description: 'Réunion mobile 0639', enabled: true },
    { id: 4, pattern: '06', replacement: '+336', description: 'France métro mobile 06', enabled: true },
    { id: 5, pattern: '07', replacement: '+337', description: 'France métro mobile 07', enabled: true },
  ]);
  const [newRule, setNewRule] = useState({ pattern: '', replacement: '', description: '' });
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [editingRule, setEditingRule] = useState({ pattern: '', replacement: '', description: '' });
  const [rewriteLoading, setRewriteLoading] = useState(false);

  // TimeZone state (SUPADMIN only)
  const [globalTimezone, setGlobalTimezone] = useState('Europe/Paris');
  const [timezoneLoading, setTimezoneLoading] = useState(false);
  
  // Complete list of IANA timezones
  const PRIORITY_TIMEZONES = [
    { value: 'Europe/Paris', label: '🇫🇷 Europe/Paris (France métropolitaine)' },
    { value: 'Indian/Reunion', label: '🇷🇪 Indian/Reunion (La Réunion)' },
    { value: 'Indian/Mayotte', label: '🇾🇹 Indian/Mayotte (Mayotte)' },
    { value: 'Indian/Mauritius', label: '🇲🇺 Indian/Mauritius (Maurice)' },
  ];
  
  const ALL_TIMEZONES = [
    // Africa
    { value: 'Africa/Abidjan', label: 'Africa/Abidjan' },
    { value: 'Africa/Accra', label: 'Africa/Accra' },
    { value: 'Africa/Algiers', label: 'Africa/Algiers' },
    { value: 'Africa/Cairo', label: 'Africa/Cairo' },
    { value: 'Africa/Casablanca', label: 'Africa/Casablanca' },
    { value: 'Africa/Dakar', label: 'Africa/Dakar' },
    { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg' },
    { value: 'Africa/Lagos', label: 'Africa/Lagos' },
    { value: 'Africa/Nairobi', label: 'Africa/Nairobi' },
    { value: 'Africa/Tunis', label: 'Africa/Tunis' },
    // America
    { value: 'America/Anchorage', label: 'America/Anchorage' },
    { value: 'America/Argentina/Buenos_Aires', label: 'America/Argentina/Buenos_Aires' },
    { value: 'America/Bogota', label: 'America/Bogota' },
    { value: 'America/Cayenne', label: '🇬🇫 America/Cayenne (Guyane)' },
    { value: 'America/Chicago', label: 'America/Chicago' },
    { value: 'America/Denver', label: 'America/Denver' },
    { value: 'America/Guadeloupe', label: '🇬🇵 America/Guadeloupe' },
    { value: 'America/Lima', label: 'America/Lima' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
    { value: 'America/Martinique', label: '🇲🇶 America/Martinique' },
    { value: 'America/Mexico_City', label: 'America/Mexico_City' },
    { value: 'America/Montreal', label: 'America/Montreal' },
    { value: 'America/New_York', label: 'America/New_York' },
    { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo' },
    { value: 'America/Toronto', label: 'America/Toronto' },
    { value: 'America/Vancouver', label: 'America/Vancouver' },
    // Asia
    { value: 'Asia/Bangkok', label: 'Asia/Bangkok' },
    { value: 'Asia/Dubai', label: 'Asia/Dubai' },
    { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong' },
    { value: 'Asia/Jakarta', label: 'Asia/Jakarta' },
    { value: 'Asia/Jerusalem', label: 'Asia/Jerusalem' },
    { value: 'Asia/Kolkata', label: 'Asia/Kolkata' },
    { value: 'Asia/Manila', label: 'Asia/Manila' },
    { value: 'Asia/Seoul', label: 'Asia/Seoul' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai' },
    { value: 'Asia/Singapore', label: 'Asia/Singapore' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
    // Atlantic
    { value: 'Atlantic/Azores', label: 'Atlantic/Azores' },
    { value: 'Atlantic/Canary', label: 'Atlantic/Canary' },
    { value: 'Atlantic/Reykjavik', label: 'Atlantic/Reykjavik' },
    // Australia
    { value: 'Australia/Adelaide', label: 'Australia/Adelaide' },
    { value: 'Australia/Brisbane', label: 'Australia/Brisbane' },
    { value: 'Australia/Melbourne', label: 'Australia/Melbourne' },
    { value: 'Australia/Perth', label: 'Australia/Perth' },
    { value: 'Australia/Sydney', label: 'Australia/Sydney' },
    // Europe
    { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam' },
    { value: 'Europe/Athens', label: 'Europe/Athens' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin' },
    { value: 'Europe/Brussels', label: 'Europe/Brussels' },
    { value: 'Europe/Bucharest', label: 'Europe/Bucharest' },
    { value: 'Europe/Budapest', label: 'Europe/Budapest' },
    { value: 'Europe/Copenhagen', label: 'Europe/Copenhagen' },
    { value: 'Europe/Dublin', label: 'Europe/Dublin' },
    { value: 'Europe/Helsinki', label: 'Europe/Helsinki' },
    { value: 'Europe/Istanbul', label: 'Europe/Istanbul' },
    { value: 'Europe/Lisbon', label: 'Europe/Lisbon' },
    { value: 'Europe/London', label: 'Europe/London' },
    { value: 'Europe/Madrid', label: 'Europe/Madrid' },
    { value: 'Europe/Moscow', label: 'Europe/Moscow' },
    { value: 'Europe/Oslo', label: 'Europe/Oslo' },
    { value: 'Europe/Paris', label: 'Europe/Paris' },
    { value: 'Europe/Prague', label: 'Europe/Prague' },
    { value: 'Europe/Rome', label: 'Europe/Rome' },
    { value: 'Europe/Stockholm', label: 'Europe/Stockholm' },
    { value: 'Europe/Vienna', label: 'Europe/Vienna' },
    { value: 'Europe/Warsaw', label: 'Europe/Warsaw' },
    { value: 'Europe/Zurich', label: 'Europe/Zurich' },
    // Indian
    { value: 'Indian/Antananarivo', label: 'Indian/Antananarivo' },
    { value: 'Indian/Maldives', label: 'Indian/Maldives' },
    { value: 'Indian/Mauritius', label: 'Indian/Mauritius' },
    { value: 'Indian/Mayotte', label: 'Indian/Mayotte' },
    { value: 'Indian/Reunion', label: 'Indian/Reunion' },
    // Pacific
    { value: 'Pacific/Auckland', label: 'Pacific/Auckland' },
    { value: 'Pacific/Fiji', label: 'Pacific/Fiji' },
    { value: 'Pacific/Guam', label: 'Pacific/Guam' },
    { value: 'Pacific/Honolulu', label: 'Pacific/Honolulu' },
    { value: 'Pacific/Noumea', label: '🇳🇨 Pacific/Noumea (Nouvelle-Calédonie)' },
    { value: 'Pacific/Tahiti', label: '🇵🇫 Pacific/Tahiti (Polynésie française)' },
    { value: 'Pacific/Wallis', label: '🇼🇫 Pacific/Wallis (Wallis-et-Futuna)' },
    // UTC
    { value: 'UTC', label: 'UTC' },
  ];

  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordRequirements, setPasswordRequirements] = useState([]);
  const [passwordValidation, setPasswordValidation] = useState({
    min_length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    special: false,
    no_sequence: true,
    match: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Unsubscribe notification settings
  const [notifSettings, setNotifSettings] = useState({
    enabled: false,
    mode: 'daily_recap',
    recap_time: '09:00',
    email_recipients: [],
    webhook_url: '',
    min_threshold: 1
  });
  const [notifLoading, setNotifLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  // SuperAdmin client selection for SMS config
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');

  // Effective client_id for SMS config (either user's client or selected by superadmin)
  const effectiveClientId = isSuperAdmin ? selectedClientId : user?.client_id;

  useEffect(() => {
    fetchPasswordRequirements();
    // SuperAdmin: fetch clients list for selection
    if (isSuperAdmin) {
      fetchClients();
    }
    if (user?.client_id) {
      fetchClientPermissions();
      fetchSmsConfig(user.client_id);
      fetchNotificationSettings();
    }
  }, [user?.client_id, isSuperAdmin]);

  // When superadmin selects a client, fetch its SMS config
  useEffect(() => {
    if (isSuperAdmin && selectedClientId) {
      fetchSmsConfig(selectedClientId);
    }
  }, [selectedClientId, isSuperAdmin]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
      // Auto-select first client if available
      if (response.data.length > 0 && !selectedClientId) {
        setSelectedClientId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  useEffect(() => {
    validatePassword(passwordData.new_password);
  }, [passwordData.new_password, passwordData.confirm_password]);

  const fetchPasswordRequirements = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/password-requirements`);
      setPasswordRequirements(response.data.requirements);
    } catch (error) {
      console.error('Failed to fetch password requirements');
    }
  };

  const fetchClientPermissions = async () => {
    if (!user?.client_id) return;
    try {
      const response = await axios.get(`${API_URL}/clients/${user.client_id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch client permissions:', error);
    }
  };

  const fetchSmsConfig = async (clientId) => {
    if (!clientId) return;
    try {
      const response = await axios.get(`${API_URL}/clients/${clientId}/sms-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmsConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch SMS config:', error);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const clientParam = user?.client_id ? `?client_id=${user.client_id}` : '';
      const response = await axios.get(`${API_URL}/v2/blacklist/notification-settings${clientParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifSettings({
        enabled: response.data.enabled || false,
        mode: response.data.mode || 'daily_recap',
        recap_time: response.data.recap_time || '09:00',
        email_recipients: response.data.email_recipients || [],
        webhook_url: response.data.webhook_url || '',
        min_threshold: response.data.min_threshold || 1
      });
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    }
  };

  const handleSaveNotificationSettings = async () => {
    setNotifLoading(true);
    try {
      const response = await axios.put(`${API_URL}/v2/blacklist/notification-settings`, {
        enabled: notifSettings.enabled,
        mode: notifSettings.mode,
        recap_time: notifSettings.recap_time,
        email_recipients: notifSettings.email_recipients,
        webhook_url: notifSettings.webhook_url || null,
        min_threshold: notifSettings.min_threshold,
        client_id: user?.client_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.warning) {
        toast.warning(response.data.warning);
      } else {
        toast.success('Paramètres de notification sauvegardés');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setNotifLoading(false);
    }
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Email invalide');
      return;
    }
    if (notifSettings.email_recipients.includes(newEmail)) {
      toast.error('Email déjà dans la liste');
      return;
    }
    setNotifSettings(prev => ({
      ...prev,
      email_recipients: [...prev.email_recipients, newEmail]
    }));
    setNewEmail('');
  };

  const handleRemoveEmail = (email) => {
    setNotifSettings(prev => ({
      ...prev,
      email_recipients: prev.email_recipients.filter(e => e !== email)
    }));
  };

  const handleSaveSmsConfig = async () => {
    if (!effectiveClientId) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    setSmsLoading(true);
    try {
      const params = {};
      // SUPADMIN can update enabled, sender_id, and custom_message
      if (isSuperAdmin) {
        params.enabled = smsConfig.enabled;
        params.sender_id = smsConfig.sender_id;
        params.custom_message = smsConfig.custom_message || '';
        
        // Also save to global SMS config
        await axios.post(`${API_URL}/v2/admin/sms-config`, {
          sender_id: smsConfig.sender_id,
          custom_message: smsConfig.custom_message || ''
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      // Both ADMIN and SUPADMIN can update credentials
      params.login = smsConfig.credentials.login;
      params.password = smsConfig.credentials.password;
      params.service_url = smsConfig.credentials.service_url;

      await axios.put(`${API_URL}/clients/${effectiveClientId}/sms-config`, null, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuration SMS B2SMS sauvegardée');
      fetchSmsConfig(effectiveClientId); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSmsLoading(false);
    }
  };

  const handleTestSms = async () => {
    if (!smsTestPhone) {
      toast.error('Entrez un numéro de téléphone pour le test');
      return;
    }
    
    // Vérifier qu'on a des credentials renseignés
    if (!smsConfig.credentials?.login || !smsConfig.credentials?.password) {
      toast.error('Veuillez renseigner votre login et mot de passe B2SMS');
      return;
    }
    
    setSmsTesting(true);
    try {
      // Appeler l'API de test SMS avec les credentials actuels
      const response = await axios.post(`${API_URL}/sms-mailing/send-test-direct`, {
        phone: smsTestPhone,
        message: 'Test B2SMS depuis B2RCS',
        sender_id: smsConfig.sender_id || 'B2RCS',
        credentials: {
          login: smsConfig.credentials.login,
          password: smsConfig.credentials.password,
          service_url: smsConfig.credentials.service_url || 'https://www.b2sms.com/v1'
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.mock) {
        toast.success('SMS de test simulé (credentials B2SMS non configurés sur le serveur)');
      } else {
        toast.success('SMS de test envoyé avec succès !');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du test SMS');
    } finally {
      setSmsTesting(false);
    }
  };

  // Check if SMS fallback is enabled for this client or if user is superadmin
  // SuperAdmin always sees the SMS section to be able to configure it
  const smsEnabled = isSuperAdmin || smsConfig.enabled;

  // Phone rewrite rule functions (SUPADMIN only)
  const loadPhoneRewriteRules = async () => {
    try {
      const response = await axios.get(`${API_URL}/v2/admin/phone-rewrite-rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.rules) {
        setPhoneRewriteRules(response.data.rules);
      }
    } catch (error) {
      console.log('Could not load phone rewrite rules, using defaults');
    }
  };

  const savePhoneRewriteRules = async () => {
    setRewriteLoading(true);
    try {
      await axios.post(`${API_URL}/v2/admin/phone-rewrite-rules`, {
        rules: phoneRewriteRules
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Règles de réécriture sauvegardées');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setRewriteLoading(false);
    }
  };

  const addRewriteRule = () => {
    if (!newRule.pattern || !newRule.replacement) {
      toast.error('Pattern et remplacement sont requis');
      return;
    }
    const newId = Math.max(...phoneRewriteRules.map(r => r.id), 0) + 1;
    setPhoneRewriteRules([...phoneRewriteRules, {
      id: newId,
      pattern: newRule.pattern,
      replacement: newRule.replacement,
      description: newRule.description || `Règle ${newId}`,
      enabled: true
    }]);
    setNewRule({ pattern: '', replacement: '', description: '' });
    toast.success('Règle ajoutée');
  };

  const removeRewriteRule = (id) => {
    setPhoneRewriteRules(phoneRewriteRules.filter(r => r.id !== id));
    toast.success('Règle supprimée');
  };

  const toggleRewriteRule = (id) => {
    setPhoneRewriteRules(phoneRewriteRules.map(r => 
      r.id === id ? { ...r, enabled: !r.enabled } : r
    ));
  };

  const startEditRule = (rule) => {
    setEditingRuleId(rule.id);
    setEditingRule({
      pattern: rule.pattern,
      replacement: rule.replacement,
      description: rule.description
    });
  };

  const cancelEditRule = () => {
    setEditingRuleId(null);
    setEditingRule({ pattern: '', replacement: '', description: '' });
  };

  const saveEditRule = () => {
    if (!editingRule.pattern || !editingRule.replacement) {
      toast.error('Pattern et remplacement sont requis');
      return;
    }
    setPhoneRewriteRules(phoneRewriteRules.map(r => 
      r.id === editingRuleId 
        ? { ...r, pattern: editingRule.pattern, replacement: editingRule.replacement, description: editingRule.description }
        : r
    ));
    setEditingRuleId(null);
    setEditingRule({ pattern: '', replacement: '', description: '' });
    toast.success('Règle modifiée');
  };

  // Move rule up in the list (higher priority)
  const moveRuleUp = (index) => {
    if (index === 0) return;
    const newRules = [...phoneRewriteRules];
    [newRules[index - 1], newRules[index]] = [newRules[index], newRules[index - 1]];
    setPhoneRewriteRules(newRules);
  };

  // Move rule down in the list (lower priority)
  const moveRuleDown = (index) => {
    if (index === phoneRewriteRules.length - 1) return;
    const newRules = [...phoneRewriteRules];
    [newRules[index], newRules[index + 1]] = [newRules[index + 1], newRules[index]];
    setPhoneRewriteRules(newRules);
  };

  // DnD-kit sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setPhoneRewriteRules((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        toast.success('Ordre des règles mis à jour');
        return newItems;
      });
    }
  };

  // Load rewrite rules on mount for superadmin
  useEffect(() => {
    if (isSuperAdmin) {
      loadPhoneRewriteRules();
      loadGlobalTimezone();
    }
  }, [isSuperAdmin]);

  // Load global timezone
  const loadGlobalTimezone = async () => {
    try {
      const response = await axios.get(`${API_URL}/v2/admin/settings/timezone`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.timezone) {
        setGlobalTimezone(response.data.timezone);
      }
    } catch (error) {
      console.log('No timezone configured, using default');
    }
  };

  // Save global timezone
  const saveGlobalTimezone = async () => {
    setTimezoneLoading(true);
    try {
      await axios.post(`${API_URL}/v2/admin/settings/timezone`, {
        timezone: globalTimezone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fuseau horaire sauvegardé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setTimezoneLoading(false);
    }
  };

  const validatePassword = (password) => {
    const hasSequence = /(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def)/i.test(password);
    
    setPasswordValidation({
      min_length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'/`~]/.test(password),
      no_sequence: !hasSequence,
      match: password === passwordData.confirm_password && password.length > 0
    });
  };

  const handleUpdateCredentials = async (e) => {
    e.preventDefault();
    if (!user?.client_id) {
      toast.error('Aucun client sélectionné');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API_URL}/clients/${user.client_id}/rcs-credentials`, credentials, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Credentials RCS mis à jour avec succès!');
      setCredentials({
        project_id: '',
        service_account_email: '',
        agent_id: '',
        brand_id: '',
        private_key: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Check all validations
    const allValid = Object.entries(passwordValidation).every(([key, value]) => value);
    if (!allValid) {
      toast.error('Veuillez respecter toutes les exigences du mot de passe');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(`${API_URL}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mot de passe modifié avec succès!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  const ValidationItem = ({ valid, text }) => (
    <div className={`flex items-center gap-2 text-sm ${valid ? 'text-green-600' : 'text-slate-500'}`}>
      {valid ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
      <span>{text}</span>
    </div>
  );

  const content = (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="settings-page">
      {!embedded && (
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Paramètres
          </h1>
          <p className="mt-2 text-slate-600">Configurez votre compte et vos accès RCS</p>
        </div>
      )}

      {/* User Info */}
      <Card className="rounded-2xl border-slate-100">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Informations du compte</CardTitle>
          <CardDescription>Vos informations de connexion</CardDescription>
        </CardHeader>
        <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-slate-600">Nom</p>
                <p className="text-lg text-slate-900" data-testid="user-name">{user?.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Email</p>
                <p className="text-lg text-slate-900" data-testid="user-email">{user?.email}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Rôle</p>
                <p className="text-lg text-slate-900" data-testid="user-role">
                  {user?.role === 'superadmin' ? 'Super Administrateur' : 
                   user?.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Password Change */}
        <Card className="rounded-2xl border-slate-100">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-indigo-50 p-2">
                <Shield className="h-6 w-6 text-indigo-700" />
              </div>
              <div>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Modifier le mot de passe</CardTitle>
                <CardDescription>Sécurisez votre compte avec un mot de passe fort</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="current-password" className="text-sm font-medium text-slate-700">
                  Mot de passe actuel
                </label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    required
                    data-testid="current-password-input"
                    className="rounded-xl border-slate-200 h-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.current ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="new-password" className="text-sm font-medium text-slate-700">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                    data-testid="new-password-input"
                    className="rounded-xl border-slate-200 h-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.new ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium text-slate-700 mb-3">Exigences du mot de passe :</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <ValidationItem valid={passwordValidation.min_length} text="Au moins 12 caractères" />
                  <ValidationItem valid={passwordValidation.uppercase} text="Une lettre majuscule" />
                  <ValidationItem valid={passwordValidation.lowercase} text="Une lettre minuscule" />
                  <ValidationItem valid={passwordValidation.digit} text="Un chiffre" />
                  <ValidationItem valid={passwordValidation.special} text="Un caractère spécial" />
                  <ValidationItem valid={passwordValidation.no_sequence} text="Pas de séquences (123, abc)" />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="confirm-password" className="text-sm font-medium text-slate-700">
                  Confirmer le nouveau mot de passe
                </label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                    data-testid="confirm-password-input"
                    className={`rounded-xl border-slate-200 h-12 pr-12 ${
                      passwordData.confirm_password && !passwordValidation.match 
                        ? 'border-red-300 focus:ring-red-500' 
                        : passwordValidation.match 
                          ? 'border-green-300 focus:ring-green-500' 
                          : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.confirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {passwordData.confirm_password && !passwordValidation.match && (
                  <p className="text-sm text-red-500">Les mots de passe ne correspondent pas</p>
                )}
              </div>

              <div className="flex justify-end pt-4 border-t border-slate-100">
                <Button
                  type="submit"
                  disabled={changingPassword || !Object.values(passwordValidation).every(v => v)}
                  className="rounded-full px-6 h-12 bg-indigo-700 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-50"
                  data-testid="change-password-button"
                >
                  <Key className="mr-2 h-5 w-5" />
                  {changingPassword ? 'Modification...' : 'Modifier le mot de passe'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* RCS Credentials - Only for Admin and SuperAdmin */}
        {(isAdmin || isSuperAdmin) && (
          <Card className={`rounded-2xl ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : 'border-slate-100'}`}>
            <CardHeader>
              <CardTitle className={isDarkMode ? 'text-[hsl(220,13%,85%)]' : ''} style={{ fontFamily: 'Manrope, sans-serif' }}>Configuration RCS</CardTitle>
              <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : ''}>Configurez vos credentials Google RCS Business Messaging</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateCredentials} className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="project-id" className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                    Project ID Google Cloud
                  </label>
                  <Input
                    id="project-id"
                    value={credentials.project_id}
                    onChange={(e) => setCredentials({ ...credentials, project_id: e.target.value })}
                    placeholder="my-project-123"
                    required
                    data-testid="project-id-input"
                    className={`rounded-xl h-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="service-email" className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                    Service Account Email
                  </label>
                  <Input
                    id="service-email"
                    type="email"
                    value={credentials.service_account_email}
                    onChange={(e) => setCredentials({ ...credentials, service_account_email: e.target.value })}
                    placeholder="rcs@my-project.iam.gserviceaccount.com"
                    required
                    data-testid="service-email-input"
                    className={`rounded-xl h-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="agent-id" className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                      Agent ID
                    </label>
                    <Input
                      id="agent-id"
                      value={credentials.agent_id}
                      onChange={(e) => setCredentials({ ...credentials, agent_id: e.target.value })}
                      placeholder="agent_123"
                      required
                      data-testid="agent-id-input"
                      className={`rounded-xl h-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="brand-id" className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                      Brand ID
                    </label>
                    <Input
                      id="brand-id"
                      value={credentials.brand_id}
                      onChange={(e) => setCredentials({ ...credentials, brand_id: e.target.value })}
                      placeholder="brand_456"
                      required
                      data-testid="brand-id-input"
                      className={`rounded-xl h-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="private-key" className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                    Private Key (JSON)
                  </label>
                  <Textarea
                    id="private-key"
                    value={credentials.private_key}
                    onChange={(e) => setCredentials({ ...credentials, private_key: e.target.value })}
                    placeholder="Collez votre clé privée JSON ici..."
                    rows={8}
                    required
                    data-testid="private-key-textarea"
                    className={`rounded-xl resize-none font-mono text-sm ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                  />
                </div>

                <div className={`flex justify-end pt-4 border-t ${isDarkMode ? 'border-[hsl(220,14%,22%)]' : 'border-slate-100'}`}>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="rounded-full px-6 h-12 bg-indigo-600 hover:bg-indigo-700 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                    data-testid="save-credentials-button"
                  >
                    <Save className="mr-2 h-5 w-5" />
                    {loading ? 'Enregistrement...' : 'Enregistrer les credentials'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* SMS Fallback (B2SMS) - Visible only if enabled for this client */}
        {smsEnabled && (isAdmin || isSuperAdmin) && (
          <Card className={`rounded-2xl ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : 'border-slate-100'}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`rounded-xl p-2 ${isDarkMode ? 'bg-amber-500/20' : 'bg-amber-50'}`}>
                    <MessageSquare className={`h-6 w-6 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <CardTitle className={isDarkMode ? 'text-[hsl(220,13%,85%)]' : ''} style={{ fontFamily: 'Manrope, sans-serif' }}>
                      SMS Fallback (B2SMS)
                    </CardTitle>
                    <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : ''}>
                      Configurez vos credentials B2SMS pour le fallback SMS
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={smsConfig.is_configured ? "default" : "secondary"} className={smsConfig.is_configured ? "bg-emerald-500" : ""}>
                    {smsConfig.is_configured ? "Configuré" : "Non configuré"}
                  </Badge>
                  <a
                    href="https://doc.b2sms.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm flex items-center gap-1 ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`}
                  >
                    Documentation <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Client Selection - SUPADMIN only */}
              {isSuperAdmin && (
                <div className="space-y-2">
                  <Label className={`font-medium ${isDarkMode ? 'text-[hsl(220,13%,85%)]' : ''}`}>
                    Sélectionner le client
                  </Label>
                  <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                    <SelectTrigger className={`rounded-xl h-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}>
                      <SelectValue placeholder="Choisir un client..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name} - {client.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Enable/Disable Toggle - SUPADMIN only */}
              {isSuperAdmin && (
                <div className={`flex items-center justify-between p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                  <div>
                    <Label className={`font-medium ${isDarkMode ? 'text-[hsl(220,13%,85%)]' : ''}`}>Activer le fallback SMS pour ce client</Label>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}`}>
                      Envoyer automatiquement un SMS si RCS échoue
                    </p>
                  </div>
                  <Switch
                    checked={smsConfig.enabled}
                    onCheckedChange={(checked) => setSmsConfig({ ...smsConfig, enabled: checked })}
                    data-testid="sms-enabled-switch"
                  />
                </div>
              )}

              {/* Sender ID - Read-only for ADMIN, editable for SUPADMIN */}
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                  Sender ID / Expéditeur
                </label>
                <div className="relative">
                  <Input
                    value={smsConfig.sender_id}
                    onChange={(e) => isSuperAdmin && setSmsConfig({ ...smsConfig, sender_id: e.target.value })}
                    placeholder="B2RCS"
                    readOnly={!isSuperAdmin}
                    data-testid="sms-sender-id-input"
                    className={`rounded-xl h-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'} ${!isSuperAdmin ? 'cursor-not-allowed opacity-70' : ''}`}
                  />
                  {!isSuperAdmin && (
                    <div className={`absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-400'}`}>
                      <Info className="h-4 w-4" />
                      <span className="text-xs">SUPADMIN</span>
                    </div>
                  )}
                </div>
                <p className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>
                  {isSuperAdmin ? "Nom qui apparaîtra comme expéditeur (max 11 caractères)" : "Géré par le SuperAdmin"}
                </p>
              </div>

              {/* Login B2SMS */}
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                  Login B2SMS
                </label>
                <Input
                  type="text"
                  value={smsConfig.credentials?.login || ''}
                  onChange={(e) => setSmsConfig({ 
                    ...smsConfig, 
                    credentials: { ...smsConfig.credentials, login: e.target.value }
                  })}
                  placeholder="Votre login B2SMS..."
                  data-testid="sms-login-input"
                  className={`rounded-xl h-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                />
              </div>

              {/* Password B2SMS */}
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                  Mot de passe B2SMS
                </label>
                <div className="relative">
                  <Input
                    type={showSmsPassword ? "text" : "password"}
                    value={smsConfig.credentials?.password || ''}
                    onChange={(e) => setSmsConfig({ 
                      ...smsConfig, 
                      credentials: { ...smsConfig.credentials, password: e.target.value }
                    })}
                    placeholder="Votre mot de passe B2SMS..."
                    data-testid="sms-password-input"
                    className={`rounded-xl h-12 pr-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSmsPassword(!showSmsPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-[hsl(220,12%,50%)] hover:text-[hsl(220,12%,70%)]' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    {showSmsPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* API URL (Advanced) */}
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                  URL de l'API (avancé)
                </label>
                <Input
                  value={smsConfig.credentials?.service_url || 'https://www.b2sms.com/v1'}
                  onChange={(e) => setSmsConfig({ 
                    ...smsConfig, 
                    credentials: { ...smsConfig.credentials, service_url: e.target.value }
                  })}
                  placeholder="https://www.b2sms.com/v1"
                  data-testid="sms-api-url-input"
                  className={`rounded-xl h-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                />
              </div>

              {/* Custom Message (SUPADMIN only) - Displayed in SMS Mailing module */}
              {isSuperAdmin && (
                <div className="space-y-2">
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                    Message personnalisé (affiché dans SMS Mailing)
                  </label>
                  <Textarea
                    value={smsConfig.custom_message || ''}
                    onChange={(e) => setSmsConfig({ ...smsConfig, custom_message: e.target.value })}
                    placeholder="Ex: Pour les campagnes de plus de 30 000 SMS, privilégiez l'envoi entre 8h et 12h..."
                    rows={3}
                    className={`rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                  />
                  <p className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>
                    Ce message sera affiché dans le module SMS Mailing. Laissez vide pour ne rien afficher.
                  </p>
                </div>
              )}

              {/* Info Box */}
              <div className={`flex items-start gap-3 p-4 rounded-xl ${isDarkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'}`}>
                <Info className={`h-5 w-5 mt-0.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                <div className={`text-sm ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-600'}`}>
                  <p className="font-medium mb-1">Hiérarchie des credentials</p>
                  <p>Si un utilisateur a ses propres credentials SMS configurés, ils seront utilisés en priorité. Sinon, les credentials du client (ci-dessus) seront utilisés par défaut.</p>
                </div>
              </div>

              {/* Test SMS */}
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                  Tester la configuration
                </label>
                <div className="flex gap-3 mt-2">
                  <Input
                    value={smsTestPhone}
                    onChange={(e) => setSmsTestPhone(e.target.value)}
                    placeholder="+33612345678"
                    data-testid="sms-test-phone-input"
                    className={`rounded-xl h-12 flex-1 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                  />
                  <Button
                    type="button"
                    onClick={handleTestSms}
                    disabled={smsTesting || !smsConfig.credentials?.login || !smsConfig.credentials?.password}
                    variant="outline"
                    className={`rounded-xl h-12 ${isDarkMode ? 'border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)] hover:bg-[hsl(220,14%,20%)]' : ''}`}
                    data-testid="sms-test-button"
                  >
                    {smsTesting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Tester
                  </Button>
                </div>
              </div>

              {/* Save Button */}
              <div className={`flex justify-end pt-4 border-t ${isDarkMode ? 'border-[hsl(220,14%,22%)]' : 'border-slate-100'}`}>
                <Button
                  type="button"
                  onClick={handleSaveSmsConfig}
                  disabled={smsLoading}
                  className="rounded-full px-6 h-12 bg-amber-500 hover:bg-amber-600 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  data-testid="save-sms-config-button"
                >
                  <Save className="mr-2 h-5 w-5" />
                  {smsLoading ? 'Enregistrement...' : 'Sauvegarder la configuration SMS'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Global Timezone Configuration - SUPADMIN Only */}
        {isSuperAdmin && (
          <Card className={`rounded-2xl ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : 'border-slate-100'}`}>
            <CardHeader className={`pb-4 ${isDarkMode ? '' : 'border-b border-slate-100'}`}>
              <CardTitle className={`text-xl font-semibold flex items-center gap-3 ${isDarkMode ? 'text-[hsl(220,13%,85%)]' : 'text-slate-800'}`}>
                <div className={`p-2.5 rounded-xl ${isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-50'}`}>
                  <Globe className={`h-6 w-6 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-500'}`} />
                </div>
                Fuseau horaire global
                <Badge className="bg-indigo-100 text-indigo-700 ml-2">SUPADMIN</Badge>
              </CardTitle>
              <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}>
                Fuseau horaire par défaut pour les campagnes programmées (serveur en Europe/RGPD)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-cyan-50 border border-cyan-200'}`}>
                <div className="flex items-start gap-3">
                  <Info className={`h-5 w-5 mt-0.5 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  <div className={`text-sm ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                    <strong>Important :</strong> Les heures de programmation des campagnes sont interprétées dans ce fuseau horaire. 
                    Le serveur convertit automatiquement vers UTC pour le déclenchement.
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <Label className={`text-base font-medium ${isDarkMode ? 'text-[hsl(220,13%,85%)]' : 'text-slate-700'}`}>
                  Fuseau horaire de la plateforme
                </Label>
                <div className="flex gap-3">
                  <Select value={globalTimezone} onValueChange={setGlobalTimezone}>
                    <SelectTrigger className={`flex-1 h-11 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}>
                      <SelectValue placeholder="Sélectionner un fuseau horaire" />
                    </SelectTrigger>
                    <SelectContent className={`max-h-80 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]' : ''}`}>
                      {/* Priority timezones */}
                      <div className={`px-2 py-1.5 text-xs font-semibold ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                        ⭐ Favoris
                      </div>
                      {PRIORITY_TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value} className={isDarkMode ? 'text-[hsl(220,13%,85%)] focus:bg-[hsl(220,14%,25%)]' : ''}>
                          {tz.label}
                        </SelectItem>
                      ))}
                      
                      {/* Separator */}
                      <div className={`my-2 border-t ${isDarkMode ? 'border-[hsl(220,14%,25%)]' : 'border-slate-200'}`} />
                      
                      {/* All timezones */}
                      <div className={`px-2 py-1.5 text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        🌍 Tous les fuseaux horaires
                      </div>
                      {ALL_TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value} className={isDarkMode ? 'text-[hsl(220,13%,85%)] focus:bg-[hsl(220,14%,25%)]' : ''}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button
                    onClick={saveGlobalTimezone}
                    disabled={timezoneLoading}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white h-11"
                  >
                    {timezoneLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Sauvegarder
                      </>
                    )}
                  </Button>
                </div>
                <p className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>
                  Heure actuelle dans ce fuseau : {new Date().toLocaleString('fr-FR', { timeZone: globalTimezone, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Phone Number Rewrite Rules - SUPADMIN Only */}
        {isSuperAdmin && (
          <Card className={`rounded-2xl ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : 'border-slate-100'}`}>
            <CardHeader className={`pb-4 ${isDarkMode ? '' : 'border-b border-slate-100'}`}>
              <CardTitle className={`text-xl font-semibold flex items-center gap-3 ${isDarkMode ? 'text-[hsl(220,13%,85%)]' : 'text-slate-800'}`}>
                <div className={`p-2.5 rounded-xl ${isDarkMode ? 'bg-purple-500/20' : 'bg-purple-50'}`}>
                  <Shield className={`h-6 w-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                </div>
                Réécriture automatique des numéros
                <Badge className="bg-indigo-100 text-indigo-700 ml-2">SUPADMIN</Badge>
              </CardTitle>
              <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}>
                Règles de conversion automatique des numéros au format international (+XX)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Existing Rules */}
              <div className="space-y-3">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                  Règles actives
                </label>
                
                {phoneRewriteRules.length === 0 ? (
                  <p className={`text-sm ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>
                    Aucune règle configurée
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className={`text-xs mb-3 ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>
                      <GripVertical className="h-3 w-3 inline mr-1" />
                      Glissez-déposez ou utilisez les flèches pour réordonner. Les règles en haut sont prioritaires.
                    </p>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={phoneRewriteRules.map(r => r.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {phoneRewriteRules.map((rule, index) => (
                          <SortableRuleItem
                            key={rule.id}
                            rule={rule}
                            index={index}
                            isDarkMode={isDarkMode}
                            editingRuleId={editingRuleId}
                            editingRule={editingRule}
                            setEditingRule={setEditingRule}
                            cancelEditRule={cancelEditRule}
                            saveEditRule={saveEditRule}
                            toggleRewriteRule={toggleRewriteRule}
                            startEditRule={startEditRule}
                            removeRewriteRule={removeRewriteRule}
                            moveRuleUp={moveRuleUp}
                            moveRuleDown={moveRuleDown}
                            totalRules={phoneRewriteRules.length}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                )}
              </div>

              {/* Add New Rule */}
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                  Ajouter une règle
                </label>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>Pattern (ex: 06)</label>
                    <Input
                      value={newRule.pattern}
                      onChange={(e) => setNewRule({...newRule, pattern: e.target.value})}
                      placeholder="06"
                      className={`rounded-xl h-10 mt-1 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                    />
                  </div>
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>Remplacement (ex: +336)</label>
                    <Input
                      value={newRule.replacement}
                      onChange={(e) => setNewRule({...newRule, replacement: e.target.value})}
                      placeholder="+336"
                      className={`rounded-xl h-10 mt-1 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                    />
                  </div>
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>Description</label>
                    <Input
                      value={newRule.description}
                      onChange={(e) => setNewRule({...newRule, description: e.target.value})}
                      placeholder="Description"
                      className={`rounded-xl h-10 mt-1 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={addRewriteRule}
                  variant="outline"
                  className={`mt-3 rounded-xl ${isDarkMode ? 'border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)] hover:bg-[hsl(220,14%,20%)]' : ''}`}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter la règle
                </Button>
              </div>

              {/* Info */}
              <div className={`flex items-start gap-3 p-4 rounded-xl ${isDarkMode ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-100'}`}>
                <Info className={`h-5 w-5 mt-0.5 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                <div className={`text-sm ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-600'}`}>
                  <p className="font-medium mb-1">Comment ça marche</p>
                  <p>Les règles sont appliquées dans l'ordre. Le pattern est cherché au début du numéro. Exemple: "06" → "+336" transforme 0612345678 en +33612345678</p>
                </div>
              </div>

              {/* Save Button */}
              <div className={`flex justify-end pt-4 border-t ${isDarkMode ? 'border-[hsl(220,14%,22%)]' : 'border-slate-100'}`}>
                <Button
                  type="button"
                  onClick={savePhoneRewriteRules}
                  disabled={rewriteLoading}
                  className="rounded-full px-6 h-12 bg-purple-500 hover:bg-purple-600 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                >
                  <Save className="mr-2 h-5 w-5" />
                  {rewriteLoading ? 'Enregistrement...' : 'Sauvegarder les règles'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notifications de désabonnement */}
        {(isAdmin || isSuperAdmin) && (
          <Card className={`rounded-2xl ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : 'border-slate-100'}`}>
            <CardHeader className={`pb-4 ${isDarkMode ? '' : 'border-b border-slate-100'}`}>
              <CardTitle className={`text-xl font-semibold flex items-center gap-3 ${isDarkMode ? 'text-[hsl(220,13%,85%)]' : 'text-slate-800'}`}>
                <div className={`p-2.5 rounded-xl ${isDarkMode ? 'bg-rose-500/20' : 'bg-rose-50'}`}>
                  <Bell className={`h-6 w-6 ${isDarkMode ? 'text-rose-400' : 'text-rose-500'}`} />
                </div>
                Notifications de désabonnement
              </CardTitle>
              <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}>
                Recevez des alertes quand des contacts se désabonnent (STOP)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                    Activer les notifications
                  </label>
                  <p className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>
                    Recevez un récapitulatif des désabonnements
                  </p>
                </div>
                <Switch 
                  checked={notifSettings.enabled}
                  onCheckedChange={(checked) => setNotifSettings({...notifSettings, enabled: checked})}
                  data-testid="notif-enabled-toggle"
                />
              </div>

              {notifSettings.enabled && (
                <>
                  {/* Mode selection */}
                  <div className="space-y-2">
                    <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                      Fréquence des notifications
                    </label>
                    <Select 
                      value={notifSettings.mode} 
                      onValueChange={(value) => setNotifSettings({...notifSettings, mode: value})}
                    >
                      <SelectTrigger className={`rounded-xl h-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily_recap">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Récapitulatif quotidien (recommandé)
                          </div>
                        </SelectItem>
                        <SelectItem value="weekly_recap">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Récapitulatif hebdomadaire
                          </div>
                        </SelectItem>
                        <SelectItem value="instant">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            Instantané (⚠️ peut être volumineux)
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {notifSettings.mode === 'instant' && (
                      <div className={`flex items-start gap-2 p-3 rounded-lg ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
                        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                        <div className={`text-sm ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                          <p className="font-medium">Attention</p>
                          <p>Le mode instantané envoie un email à chaque désabonnement. Sur une campagne de 100 000 envois avec 10% de STOP, vous recevrez 10 000 emails ! Nous recommandons le récapitulatif quotidien.</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recap time (for daily/weekly) */}
                  {notifSettings.mode !== 'instant' && (
                    <div className="space-y-2">
                      <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                        Heure d'envoi du récapitulatif
                      </label>
                      <Input
                        type="time"
                        value={notifSettings.recap_time}
                        onChange={(e) => setNotifSettings({...notifSettings, recap_time: e.target.value})}
                        className={`rounded-xl h-12 w-40 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                      />
                    </div>
                  )}

                  {/* Email recipients */}
                  <div className="space-y-2">
                    <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                      Destinataires des notifications
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="email@exemple.com"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmail())}
                        className={`rounded-xl h-12 flex-1 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                      />
                      <Button 
                        type="button" 
                        onClick={handleAddEmail}
                        variant="outline"
                        className={`rounded-xl h-12 ${isDarkMode ? 'border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : ''}`}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Email list */}
                    {notifSettings.email_recipients.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {notifSettings.email_recipients.map((email, idx) => (
                          <Badge 
                            key={idx} 
                            variant="secondary" 
                            className={`py-1 pl-3 pr-1 flex items-center gap-1 ${isDarkMode ? 'bg-[hsl(220,14%,20%)] text-[hsl(220,13%,75%)]' : ''}`}
                          >
                            <Mail className="h-3 w-3 mr-1" />
                            {email}
                            <button 
                              onClick={() => handleRemoveEmail(email)}
                              className="ml-1 p-1 rounded hover:bg-red-500/20"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {notifSettings.email_recipients.length === 0 && (
                      <p className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>
                        Ajoutez au moins un email pour recevoir les notifications
                      </p>
                    )}
                  </div>

                  {/* Webhook URL (advanced) */}
                  <div className="space-y-2">
                    <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,70%)]' : 'text-slate-700'}`}>
                      Webhook URL (optionnel)
                    </label>
                    <Input
                      type="url"
                      value={notifSettings.webhook_url}
                      onChange={(e) => setNotifSettings({...notifSettings, webhook_url: e.target.value})}
                      placeholder="https://votresite.com/webhook/unsubscribe"
                      className={`rounded-xl h-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-[hsl(220,13%,85%)]' : 'border-slate-200'}`}
                    />
                    <p className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>
                      Recevez les désabonnements en temps réel via webhook (POST JSON)
                    </p>
                  </div>

                  {/* Save Button */}
                  <div className={`flex justify-end pt-4 border-t ${isDarkMode ? 'border-[hsl(220,14%,22%)]' : 'border-slate-100'}`}>
                    <Button
                      type="button"
                      onClick={handleSaveNotificationSettings}
                      disabled={notifLoading || (notifSettings.enabled && notifSettings.email_recipients.length === 0)}
                      className="rounded-full px-6 h-12 bg-rose-500 hover:bg-rose-600 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="save-notif-settings-button"
                    >
                      <Save className="mr-2 h-5 w-5" />
                      {notifLoading ? 'Enregistrement...' : 'Sauvegarder les notifications'}
                    </Button>
                  </div>
                  {notifSettings.enabled && notifSettings.email_recipients.length === 0 && (
                    <p className={`text-xs text-center ${isDarkMode ? 'text-rose-400' : 'text-rose-500'}`}>
                      Ajoutez au moins un email pour sauvegarder
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
  );

  if (embedded) {
    return content;
  }

  return <Layout>{content}</Layout>;
};

export default Settings;
